import React from 'react';
import parse from 'html-react-parser';
import { Box, Typography, Stack, Breadcrumbs } from '@mui/material';
import { neutral } from '../../_foundations/phxColors';
import TruncatedText from '../TruncatedText';

export interface PageHeaderProps {
  title: string | React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  titleBadge?: React.ReactNode;
  breadcrumbItems?: React.ReactNode[];
}

const renderTitle = (title: PageHeaderProps['title']) => {
  if (typeof title !== 'string') return title;

  // `parse()` returns the original string when there is no HTML to parse;
  // in that case we can safely truncate. When it returns React elements
  // (HTML entities/tags), keep the existing wrapper so HTML still renders.
  const parsed = parse(title);
  if (typeof parsed === 'string') {
    return (
      <TruncatedText
        text={parsed}
        typographyProps={{ variant: 'displaySm', color: 'text.primary' }}
      />
    );
  }

  return (
    <Typography
      variant='displaySm'
      color='text.primary'
    >
      {parsed}
    </Typography>
  );
};

export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  actions,
  titleBadge,
  breadcrumbItems = [],
}) => {
  return (
    <Box
      sx={{
        py: 4,
        px: 5,
        borderBottom: '1px solid',
        borderColor: neutral[100],
        bgcolor: neutral[50],
      }}
    >
      {breadcrumbItems && breadcrumbItems.length > 0 && (
        <Breadcrumbs
          aria-label='breadcrumb'
          sx={{ mb: 5 }}
        >
          {breadcrumbItems}
        </Breadcrumbs>
      )}
      <Stack
        direction='row'
        justifyContent='space-between'
        alignItems='center'
        spacing={2}
      >
        <Stack
          direction='row'
          spacing={2}
          alignItems='center'
          sx={{ flex: 1, minWidth: 0 }}
        >
          {renderTitle(title)}
          {titleBadge}
        </Stack>
        {actions && <Box sx={{ flexShrink: 0 }}>{actions}</Box>}
      </Stack>
      {subtitle && (
        <Box sx={{ mt: 2 }}>
          {typeof subtitle === 'string' ? (
            <Typography
              variant='bodyMd'
              color='text.secondary'
            >
              {parse(subtitle)}
            </Typography>
          ) : (
            subtitle
          )}
        </Box>
      )}
    </Box>
  );
};
